import React, { useMemo } from 'react';
import { useEffectiveAddons } from '../../contexts/effective-features/EffectiveFeaturesContext';
import { Dropdown } from 'primereact/dropdown';
import { useAccount } from '../../contexts/account/AccountContexts';
import DropdownItem, { DropdownItemData } from '../../components/dropdown-item/DropdownItem';
import useChangeProjectKeyword from '../../hooks/filters/UseChangeProjectKeyword';
import { useGetCommonKeywordsWithTags } from '../../api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { getTagLabel } from '../../helpers/tags/TagsHelper';
import { resolveBrandKey } from '../../helpers/keyword/KeywordHelper';
import { useGetTags } from '../../api/hooks/tags/useGetTags';
import { useTranslation } from 'react-i18next';
import { TAG_NAME } from '../../utils/urls';
import CustomTooltip from '../common/tooltip/CustomTooltip';
import { Button } from 'primereact/button';
import { ProjectResponse } from '../../models/projects/ProjectResponse';
import usePermission from '../../hooks/global/UsePermission';

interface ChangeKeywordBoxProps {
  project: ProjectResponse;
  changeKeyword?: () => void;
  // Tag-resolved keys of "live" brands (data under current models). When provided, orphan brands are
  // hidden from the dropdown — except the project's current brand, which is always kept selectable.
  liveKeys?: string[];
}

const ChangeKeywordBox: React.FC<ChangeKeywordBoxProps> = ({ project, changeKeyword, liveKeys }) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const projectsAccess = usePermission('PROJECTS');
  // Z KEYWORDS_PAGE użytkownik sam decyduje, co śledzi (strona Brands, wybór marek do analizy), a lista
  // z backendu to już tylko marki z aktywną nazwą — więc bez filtra „ma dane”: włączona marka ma dać się
  // wybrać także bez wzmianek w okresie (np. BOŚ z wyłączoną nazwą główną i aktywnym „BOŚ Bank”).
  const hasBrandStatus = useEffectiveAddons().includes('KEYWORDS_PAGE');

  const onRefresh = () => {
    reloadTags();
    reloadCommonKeywords();
    if (changeKeyword) {
      changeKeyword();
    }
  };

  const apiProjectId = project.id;
  const { tags, refetch: reloadTags } = useGetTags(apiProjectId);
  const { commonKeywords, refetch: reloadCommonKeywords } = useGetCommonKeywordsWithTags(apiProjectId);

  const { changeProjectKeyword } = useChangeProjectKeyword({
    onRefresh,
  });

  const projectId = project.id;

  const brand = useMemo(() => {
    if (!project) {
      return '';
    }
    return project.firmName || '';
  }, [project]);

  const defaultKeyword = project.defaultKeyword;

  const keywordsDataOptions: DropdownItemData<string>[] = useMemo(() => {
    const liveSet = new Set(liveKeys ?? []);
    // Only filter when we actually have a live set (avoid hiding everything during load).
    const applyLiveFilter = liveSet.size > 0 && !hasBrandStatus;

    // Canonical forms — firmName/defaultKeyword may hold a tag key or a raw display-cased keyword.
    const firmBrandKey = resolveBrandKey(project.firmName, tags);
    const defaultBrandKey = resolveBrandKey(defaultKeyword, tags);

    const options = commonKeywords
      .filter((data) => {
        if (!applyLiveFilter) {
          return true;
        }
        const resolvedKey = data.tagId ? `${TAG_NAME}${data.tagId}` : data.normalizedKeyword;
        // keep live brands + the project's current brand + the project default keyword
        // (all always selectable, even if orphaned, so the user can always return to the default)
        return (
          liveSet.has(resolvedKey) ||
          resolvedKey === firmBrandKey ||
          resolvedKey === defaultBrandKey
        );
      })
      .map((data) => ({
        name: getTagLabel(tags, data.tagId, data.keyword),
        type: data.normalizedKeyword,
      }));

    const uniqueOptions = options.filter(
      (option, index, self) => index === self.findIndex((o) => o.type === option.type)
    );

    // Always keep the project's current brand + default keyword selectable, even when they are
    // orphaned (not live) or not a project keyword. Without this they'd vanish from the picker — the
    // commonKeywords rows get filtered out and the Dropdown value renders empty.
    //
    // The option's `type` must match what the Dropdown selects by (`brand` = firmName, which for a
    // tagged brand is the TAG KEY `chatbeat_tag_name_<id>`, e.g. set by reset-to-default). So resolve
    // the key to its tag form when it belongs to a tag: the tags-resolved `commonKeywords` already
    // keys tagged entries by their tag key, and `firmName`/`defaultKeyword` can be either a raw
    // keyword (→ look it up in `tags`) or already a tag key (→ just resolve the label).
    const ensureSelectable = (key: string | undefined) => {
      if (!key) return;
      let optionType = key;
      let optionName = key;
      if (key.startsWith(TAG_NAME)) {
        const tagId = Number(key.slice(TAG_NAME.length));
        optionName = getTagLabel(tags, tagId, key);
      } else {
        const tag = tags.find((tr) =>
          tr.keywordsData?.some((kd) => kd.normalizedKeyword === key)
        );
        if (tag) {
          optionType = `${TAG_NAME}${tag.id}`;
          optionName = getTagLabel(tags, tag.id, key);
        }
      }
      if (uniqueOptions.some((option) => option.type === optionType)) return;
      uniqueOptions.push({ name: optionName, type: optionType });
    };
    ensureSelectable(project.firmName);
    ensureSelectable(defaultKeyword);

    return uniqueOptions;
  }, [commonKeywords, tags, liveKeys, project.firmName, defaultKeyword, hasBrandStatus]);

  // Zmiana marki zapisuje się na projekcie (kaskadą po monitorach), więc jest edycją — dla widza bez
  // prawa zapisu (dziś: gość) wybierak po prostu znika, tak jak reszta akcji zapisu.
  if (!accountResponse || !projectsAccess.write || !project || !commonKeywords.length) {
    return <></>;
  }

  const defaultTagsData = tags.find((tr) =>
    tr.keywordsData.find((kd) => kd.normalizedKeyword === defaultKeyword)
  );

  const isResetDisabled =
    brand === defaultKeyword ||
    commonKeywords.length <= 1 ||
    brand === `${TAG_NAME}${defaultTagsData?.id}`;

  return (
    <div className="change-keyword-box">
      <Dropdown
        // Exact match first (as always); fallback: firmName may be a raw member of a tag while the
        // options key it by the tag (ensureSelectable).
        value={
          keywordsDataOptions.find((option) => option.type === brand) ||
          keywordsDataOptions.find((option) => option.type === resolveBrandKey(brand, tags)) ||
          null
        }
        onChange={(e) => changeProjectKeyword(projectId, e.value.type)}
        options={keywordsDataOptions}
        className={`dropdown`}
        optionLabel="name"
        valueTemplate={(option) => (option ? <span className="capitalize">{option.name}</span> : null)}
        itemTemplate={(option) => <DropdownItem name={option.name} className="capitalize" />}
        filter
        panelClassName="dropdown__footer"
        panelFooterTemplate={
          <>
            <Button
              className={`btn btn--sm ${isResetDisabled ? 'btn--disabled-tooltip-color' : ''}`}
              id={`btn-default-name-${projectId}`}
              label={t('resetToDefault')}
              onClick={() => {
                if (isResetDisabled) {
                  return;
                }

                const newKeyword =
                  defaultTagsData && defaultTagsData.id
                    ? `${TAG_NAME}${defaultTagsData.id}`
                    : defaultKeyword;
                changeProjectKeyword(projectId, newKeyword);
              }}
            />
            {isResetDisabled && (
              <CustomTooltip
                tooltipText="defaultBrandAlreadyActive"
                targetRef={`btn-default-name-${projectId}`}
              />
            )}
          </>
        }
      />
    </div>
  );
};

export default ChangeKeywordBox;
